import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import TemuOpsListToolbar from '@/temu-platform/components/TemuOpsListToolbar';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import TemuSwitch from '@/temu-platform/components/TemuSwitch';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import CategoryCascaderSelect from '@/temu-platform/components/CategoryCascaderSelect';
import RrpTemplateCreateModal from '@/temu-platform/modules/templates/RrpTemplateCreateModal';
import { formatBeijingDateTime } from '@/temu-platform/utils/formatBeijingDateTime';
import { cellDisplayTitle } from '@/temu-platform/utils/cellDisplayTitle';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import { flattenLeafCategories } from '@/temu-platform/utils/temuCategoryTree';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/temu-platform/utils/temuTemplateVersionGuard';
import { useTemuPageShopScope } from '@/temu-platform/hooks/useTemuPageShopScope';
import { usePlatformShopPick } from '@/hooks/usePlatformShopPick';
import { temuPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

function rowKey(row) {
  return String(row?.id ?? '');
}

const COL_SPAN = 8;

/** 模板库 · 建议价资料库 */
export default function RrpTemplatePage() {
  const { t } = useTranslation('workbench');
  const {
    shopDimensionTabs,
    concreteScopeId,
    allShopsScope,
    scopedShops = [],
  } = useTemuPageShopScope();
  const shopId = concreteScopeId;
  const { pickShop, shopPickModal } = usePlatformShopPick({
    shops: scopedShops,
    initialShopId: shopId,
  });
  const ensureWriteShopId = useCallback(async ({
    title = t('productTemplate.pickShopTitle'),
    description = t('productTemplate.pickShopDesc'),
    confirmText = t('productTemplate.confirm'),
  } = {}) => {
    if (shopId) return String(shopId);
    return pickShop({ title, description, confirmText, initialShopId: '' });
  }, [shopId, pickShop, t]);
  const [modalShopId, setModalShopId] = useState('');

  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [categoryIdDraft, setCategoryIdDraft] = useState('');
  const [productTypeIdDraft, setProductTypeIdDraft] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [categories, setCategories] = useState([]);
  const [queryNonce, setQueryNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  useEffect(() => {
    if (!shopId) {
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopCategories(shopId)
      .then((res) => {
        if (cancelled) return;
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => { cancelled = true; };
  }, [shopId]);

  const load = useCallback(async () => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setSelectedKeys(new Set());
      return;
    }
    setLoading(true);
    try {
      const res = await temuPlatformAPI.listRrpTemplates(shopId, {
        keyword: keyword || undefined,
        productTypeId: productTypeId || undefined,
        page,
        pageSize,
      });
      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total) || 0);
    } catch (e) {
      setItems([]);
      setTotal(0);
      showToast(e?.message || t('productTemplate.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, keyword, productTypeId, page, pageSize, queryNonce, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedKeys.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      const key = rowKey(row);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
  }, [items, selectedKeys]);

  const selectableKeys = useMemo(
    () => items.map(rowKey).filter(Boolean),
    [items],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onCategoryChange = (id, meta) => {
    const nextCategoryId = id ? String(id) : '';
    const hit = categoryOptions.find((opt) => String(opt.value) === nextCategoryId);
    // 类目缓存只存一级，末级由级联懒加载返回，categoryOptions 里可能查不到 → 用 meta 兜底
    const metaTypeId = meta?.productTypeId != null && String(meta.productTypeId) !== '0'
      ? String(meta.productTypeId)
      : '';
    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
      ? String(hit.productTypeId)
      : (metaTypeId || nextCategoryId);
    setCategoryIdDraft(nextCategoryId);
    setProductTypeIdDraft(nextTypeId);
  };

  const runQuery = () => {
    setKeyword(keywordDraft.trim());
    setProductTypeId(productTypeIdDraft);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resetQuery = () => {
    setKeywordDraft('');
    setKeyword('');
    setCategoryIdDraft('');
    setProductTypeIdDraft('');
    setProductTypeId('');
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resolveSelectedRows = () => {
    const map = selectedRowsRef.current;
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  };

  const onBatchDelete = async () => {
    if (!shopId || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToDelete'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdDelete'), 'error');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('returnList.batchDelete'),
      message: t('rrpLib.batchDeleteMsg', { count: ids.length }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await temuPlatformAPI.batchDeleteRrpTemplates(shopId, ids);
      const deleted = Number(res?.deleted);
      showToast(t('productTemplate.deletedCount', { count: Number.isFinite(deleted) ? deleted : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onDeleteOne = async (row) => {
    if (!shopId || !row?.id) return;
    const ok = await showTemuConfirm({
      title: t('productTemplate.deleteTitle'),
      message: t('productTemplate.deleteOne', { name: row.name || row.id }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await temuPlatformAPI.deleteRrpTemplate(shopId, row.id);
      showToast(t('productTemplate.deleted'), 'success');
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey(row));
        return next;
      });
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onToggleAutoRule = async (row, enabled) => {
    if (!shopId || !row?.id) return;
    const id = row.id;
    const key = String(id);
    const prevEnabled = row.autoRuleEnabled === true || row.autoRuleEnabled === 1;
    setTogglingIds((prev) => new Set(prev).add(key));
    setItems((prev) => prev.map((item) => (
      String(item.id) === key ? { ...item, autoRuleEnabled: enabled } : item
    )));
    try {
      const body = { autoRuleEnabled: enabled };
      const version = parseTemplateVersion(row.version);
      if (version != null) body.version = version;
      const res = await temuPlatformAPI.updateRrpTemplate(shopId, id, body);
      const nextVersion = parseTemplateVersion(res?.item?.version);
      setItems((prev) => prev.map((item) => (
        String(item.id) === key
          ? {
            ...item,
            autoRuleEnabled: enabled,
            ...(nextVersion != null ? { version: nextVersion } : {}),
          }
          : item
      )));
      showToast(enabled ? t('productTemplate.autoOn') : t('productTemplate.autoOff'), 'success');
    } catch (e) {
      setItems((prev) => prev.map((item) => (
        String(item.id) === key ? { ...item, autoRuleEnabled: prevEnabled } : item
      )));
      if (isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'rrp' }]), 'warning', 3000);
        try {
          const res = await temuPlatformAPI.getRrpTemplate(shopId, id);
          const item = res?.item || res || {};
          const serverEnabled = item.autoRuleEnabled === true || item.autoRuleEnabled === 1;
          const serverVersion = parseTemplateVersion(item.version);
          setItems((prev) => prev.map((itemRow) => (
            String(itemRow.id) === key
              ? {
                ...itemRow,
                autoRuleEnabled: serverEnabled,
                ...(serverVersion != null ? { version: serverVersion } : {}),
              }
              : itemRow
          )));
        } catch {
          // 单行刷新失败时保持回滚状态
        }
      } else {
        showToast(e?.message || t('productTemplate.autoUpdateFailed'), 'error');
      }
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const emptyHint = !shopId
    ? t('returnList.pickShop')
    : (loading
      ? t('returnList.loading')
      : (keyword || productTypeId ? t('rrpLib.noMatch') : t('productTemplate.empty')));

  return (
    <>
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || deleting}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId || deleting}
            extraActions={[
              {
                key: 'create',
                label: t('rrpLib.create'),
                tone: 'primary',
                disabled: (!shopId && !allShopsScope) || loading || deleting,
                onClick: async () => {
                  const writeShopId = await ensureWriteShopId({
                    title: t('productTemplate.createPickTitle'),
                    description: t('rrpLib.createPickDesc'),
                    confirmText: t('productTemplate.createHere'),
                  });
                  if (!writeShopId) {
                    showToast(t('returnList.pickShop'), 'warning');
                    return;
                  }
                  setModalShopId(writeShopId);
                  setEditingId(null);
                  setModalOpen(true);
                },
              },
            ]}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--category">
              <span className="tpo-field__label">{t('productTemplate.colCategory')}</span>
              <CategoryCascaderSelect
                shopId={shopId}
                categories={categories}
                value={categoryIdDraft}
                disabled={!shopId || loading}
                placeholder={!shopId ? t('returnList.pickShop') : t('productTemplate.allCategories')}
                emptyText={!shopId ? t('returnList.pickShop') : t('productTemplate.noCategories')}
                onChange={onCategoryChange}
              />
            </div>
            <div className="tpo-field tpo-field--inline">
              <span className="tpo-field__label">{t('productTemplate.colName')}</span>
              <input
                className="tpo-input"
                value={keywordDraft}
                disabled={!shopId}
                placeholder={t('rrpLib.nameFilterPh')}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runQuery();
                }}
              />
            </div>
          </div>
          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--ghost tpo-btn--sm"
              disabled={!shopId || loading}
              onClick={resetQuery}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!shopId || loading}
              onClick={runQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={shopDimensionTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('productTemplate.querying')}
      >
        <div className="tpo-drafts__table-wrap tpo-certs-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-certs-table tpo-tpl-table">
            <colgroup>
              <col className="tpo-certs-col tpo-certs-col--check" />
              <col className="tpo-certs-col tpo-certs-col--name" />
              <col className="tpo-certs-col tpo-certs-col--category" />
              <col className="tpo-certs-col tpo-certs-col--rest" />
              <col className="tpo-certs-col tpo-certs-col--rest" />
              <col className="tpo-certs-col tpo-certs-col--rest" />
              <col className="tpo-certs-col tpo-certs-col--rest" />
              <col className="tpo-certs-col tpo-certs-col--rest" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-certs-th tpo-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('productTemplate.selectAll')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col" className="tpo-certs-th">{t('productTemplate.colName')}</th>
                <th scope="col" className="tpo-certs-th">{t('productTemplate.colCategory')}</th>
                <th scope="col" className="tpo-certs-th">{t('rrpLib.colMarkup')}</th>
                <th scope="col" className="tpo-certs-th tpo-certs-th--switch">{t('productTemplate.colAutoRule')}</th>
                <th scope="col" className="tpo-certs-th tpo-certs-th--order">{t('productTemplate.colPriority')}</th>
                <th scope="col" className="tpo-certs-th">{t('productTemplate.colUpdatedAt')}</th>
                <th scope="col" className="tpo-certs-th tpo-certs-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || items.length === 0 ? (
                <tr>
                  <td colSpan={COL_SPAN} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">{emptyHint}</p>
                    </div>
                  </td>
                </tr>
              ) : items.map((row) => {
                const key = rowKey(row);
                return (
                  <tr key={key}>
                    <td
                      className="tpo-certs-td tpo-certs-td--check"
                      onClick={() => toggleOne(key)}
                    >
                      <input
                        type="checkbox"
                        aria-label={t('returnList.selectRow', { title: row.name || key })}
                        checked={selectedKeys.has(key)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleOne(key)}
                      />
                    </td>
                    <td className="tpo-certs-td" title={cellDisplayTitle(row.name, row.remark)}>
                      <strong>{row.name || '—'}</strong>
                      {row.remark ? <div className="tpo-muted">{row.remark}</div> : null}
                    </td>
                    <td className="tpo-certs-td" title={cellDisplayTitle(row.categoryPathLabel || row.productTypeId)}>
                      {row.categoryPathLabel || row.productTypeId || '—'}
                    </td>
                    <td className="tpo-certs-td">
                      {row.markupPercent != null && row.markupPercent !== '' ? `${row.markupPercent}%` : '—'}
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--switch">
                      <TemuSwitch
                        checked={!!row.autoRuleEnabled}
                        disabled={deleting || togglingIds.has(key)}
                        ariaLabel={t('productTemplate.autoRuleAria', { name: row.name || t('productTemplate.templateFallback') })}
                        onChange={(next) => onToggleAutoRule(row, next)}
                      />
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--order">{row.sortOrder ?? 0}</td>
                    <td className="tpo-certs-td tpo-certs-td--time" title={cellDisplayTitle(formatBeijingDateTime(row.updatedAt))}>
                      {formatBeijingDateTime(row.updatedAt)}
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--ops">
                      <div className="temu-draft-actions">
                        <button
                          type="button"
                          className="wf-tasks-actions__link"
                          disabled={deleting}
                          onClick={() => {
                            setEditingId(row.id);
                            setModalOpen(true);
                          }}
                        >
                          {t('productTemplate.edit')}
                        </button>
                        <button
                          type="button"
                          className="wf-tasks-actions__link"
                          disabled={deleting}
                          onClick={() => onDeleteOne(row)}
                        >
                          {t('returnList.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('rrpLib.pager')}
        />
      </ProductListCard>

      <RrpTemplateCreateModal
        open={modalOpen}
        shopId={modalShopId || shopId}
        templateId={editingId}
        onClose={() => {
          setModalOpen(false);
          setEditingId(null);
          setModalShopId('');
        }}
        onSaved={() => {
          setPage(1);
          setQueryNonce((n) => n + 1);
        }}
      />
      {shopPickModal}
    </>
  );
}
